'use client';

import { useRef, useEffect, forwardRef } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import FormHelperText from '@mui/material/FormHelperText';
import Editor from '@toast-ui/editor';
import colorSyntax from '@toast-ui/editor-plugin-color-syntax';

import '@toast-ui/editor/dist/toastui-editor.css';
import 'tui-color-picker/dist/tui-color-picker.css';
import '@toast-ui/editor-plugin-color-syntax/dist/toastui-editor-plugin-color-syntax.css';

export const ToastUIEditor = forwardRef(
    (
        {
            value = '', // Markdown content controlled by react-hook-form
            onChange, // onChange function from react-hook-form
            width = '100%',
            height = '500px',
            editable = true,
            helperText,
            resetValue,
            error,
            ...other
        },
        ref // The ref forwarded from parent components
    ) => {
        const editorRef = useRef(null);

        useEffect(() => {
            if (!editorRef.current) {
                // Initialize Toast UI Editor only once
                const editorInstance = new Editor({
                    plugins: [colorSyntax],
                    el: document.querySelector('#toast-editor'),
                    height,
                    initialEditType: 'wysiwyg',
                    previewStyle: 'vertical',
                    initialValue: value, // Set initial value
                    usageStatistics: false, // Disable analytics (optional),
                    events: {
                        change: () => {
                            const markdown = editorInstance.getMarkdown();
                            onChange(markdown); // Trigger onChange to update react-hook-form
                        },
                    },
                    ...other,
                });
                editorRef.current = editorInstance;

                // If we need to access the editor instance outside this component, forward the ref
                if (ref) {
                    ref.current = editorInstance;
                }
            }

            // Reset the editor when resetValue is triggered (e.g., after form submission)
            if (resetValue && editorRef.current) {
                editorRef.current.setMarkdown(value);
            }

            return () => {
                if (editorRef.current) {
                    editorRef.current.destroy();
                    editorRef.current = null;
                }
            };
        }, [value, height, resetValue, onChange, other]);

        return (
            <Stack sx={{ width }}>
                <Box
                    sx={{
                        width: '100%',
                        height,
                        borderRadius: 2,
                        border: (theme) => `dashed 1px ${theme.vars.palette.divider}`,
                    }}
                >
                    <div id="toast-editor" />
                </Box>

                {helperText && (
                    <FormHelperText error={!!error} sx={{ mt: 1 }}>
                        {helperText}
                    </FormHelperText>
                )}
            </Stack>
        );
    }
);
